import * as React from 'react'
import { useParams, Link } from 'react-router-dom'
import { fetchData } from '../utils/fetchData'
import Page from '../components/layout/Page'
import Content from '../components/layout/Content'
import Folder from '../components/Folder'
import ProtectedRoute from '../helpers/ProtectedRoute'
import Arrow from '../icons/Arrow'
import { Media } from '../types/User'
import styles from '../styles/Store.module.css'
import { channelDisplayNames } from '../helpers/channelName'
import { useStoreContext } from '../context/StoreContext'

function Store() {
  const { store, setStore, channelFolders, setChannelFolders } = useStoreContext()
  const STORAGE_KEY = 'media'
  const localStorageMedia = localStorage.getItem(STORAGE_KEY)
  let media: Media | null = null
  if (localStorageMedia) {
    try {
      media = JSON.parse(localStorageMedia)
    } catch (error) {
      console.log(error)
    }
  }
  const token = media?.token ?? null
  const { storeId } = useParams()
  const [loading, setLoading] = React.useState<boolean>(false)

  React.useEffect(() => {
    async function getChannelFolders() {
      if (!token || !storeId) {
        localStorage.removeItem(STORAGE_KEY)
        setLoading(false)
        return
      }

      // Do not fetch if channelFolders is already populated in our store context, and the store matches the current storeId
      if (channelFolders.length > 0 && store && store.id === storeId) {
        return
      }
      
      setLoading(true)

      try {
        // Fetch active channels for the store
        const storeRes = await fetchData(`/api/cloudinary/store/${storeId}`, { method: "GET" })
        if (storeRes?.status === 200) {
          setStore(storeRes.data)
          setChannelFolders(storeRes.data.channelTypes.map(channel => channel.headerValue))
          if (storeRes.data.channelTypes.length === 4) {
            // All 4 channels active, no need to fetch channel tags from uploaded images
            return
          }
        } else {
          console.log("Failed to fetch store data", storeRes)
        }

        // Fetch channels for the store based on uploaded images
        const channelTagRes = await fetchData(`/api/cloudinary/channelTags/${storeId}`, { method: "GET" })
        if (channelTagRes?.status === 200) {
          setChannelFolders(prev => {
            const existingChannels = new Set(prev)
            const filteredData = channelTagRes.data.filter(item => !existingChannels.has(item))
            return [...prev, ...filteredData]
          })
        } else {
          console.log("Failed to fetch channel data from uploaded images", channelTagRes)
        }
      } catch (error) {
        console.log("Error fetching channel folders", error)
      } finally {
        setLoading(false)
      }
    }

    getChannelFolders()
  }, [token, storeId, channelFolders.length, store?.id])

  return (
    <ProtectedRoute user={token}>
      <Page>
        <div className={styles.store}>
          <Content>
            {media?.stores && media?.stores?.length > 1 &&
              <Link className={styles.backButton} to={`/`}>
                <Arrow className={styles.arrowIcon} />
                <span>Till alla butiker</span>
              </Link>}
            {loading ? (
              <div>Laddar…</div>
            ) : (
              <>
                <h1 className={styles.heading}>{store?.name || ''}</h1>
                {store?.channelTypes && store?.channelTypes.length > 0 && (
                  <ul className={styles.folders}>
                    <li>
                      <Folder href={`/${storeId}/global`} name="Globala" />
                    </li>
                    {channelFolders.map((channel: string) => (
                      <li key={channel}>
                        <Folder
                          href={`/${storeId}/${channel}`}
                          name={channelDisplayNames[channel] || channel}
                        />
                      </li>
                    ),
                    )}
                    <li>
                      <Folder href={`/${storeId}/other`} name="Övrigt" />
                    </li>
                  </ul>
                )}
              </>
            )}
          </Content>
        </div>
      </Page>
    </ProtectedRoute>
  )
}

export default Store
